import { Button, PageError, PageLoading, TextButton } from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { Dialog, DialogContent } from "@majitap/ui/arc/dialog/dialog";
import { Input } from "@majitap/ui/arc/input/input";
import { Select } from "@majitap/ui/arc/select/select";
import { SortableDataTable } from "@majitap/ui/arc/sortable-data-table/sortable-data-table";
import { Switch } from "@majitap/ui/arc/switch/switch";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useState } from "react";
import { errorOf } from "../../api";
import { messageFor } from "../../error-copy";
import { CATEGORY, type ChargeType, chargeTypesQuery, PAYEE, pricingActions } from "../../pricing";
import { ConfirmDialog } from "../shared/confirm";
import styles from "./pricing.module.css";

type Category = ChargeType["category"];
type Payee = ChargeType["payee"];

/**
 * Charge types: the kinds of money a rule can apply (a price, a fee, a cost, a tax). The system
 * ones are what pricing and the books rely on: they can be renamed, nothing else.
 */
export function ChargeTypesView(): ReactNode {
  const types = useQuery(chargeTypesQuery);
  const [dialog, setDialog] = useState<
    { kind: "edit"; type?: ChargeType } | { kind: "remove"; type: ChargeType } | null
  >(null);
  const queryClient = useQueryClient();
  if (types.status === "pending") return <PageLoading label="Loading charge types" />;
  if (types.status === "error")
    return <PageError message={messageFor(errorOf(types.error))} onRetry={() => types.refetch()} />;
  return (
    <div className={styles.panel}>
      <div className={styles.actions}>
        <Button variant="secondary" size="sm" onClick={() => setDialog({ kind: "edit" })}>
          Add a charge type
        </Button>
      </div>
      <SortableDataTable
        caption="Charge types"
        rowKey="id"
        rows={types.data}
        itemName={{ one: "charge type", other: "charge types" }}
        columns={[
          {
            key: "name",
            label: "Charge type",
            sortable: false,
            render: (_v, t) => (
              <span>
                {t.name} {t.is_system ? <Badge tone="info">System</Badge> : null}
              </span>
            ),
          },
          {
            key: "code",
            label: "Code",
            sortable: false,
            render: (_v, t) => <span className={styles.mono}>{t.code}</span>,
          },
          { key: "category", label: "Kind", sortable: false, render: (_v, t) => CATEGORY[t.category] },
          {
            key: "direction",
            label: "Money",
            sortable: false,
            render: (_v, t) =>
              t.direction === "receivable" ? "Charged to the customer" : `Paid to ${PAYEE[t.payee].toLowerCase()}`,
          },
          {
            key: "is_active",
            label: "Status",
            sortable: false,
            render: (_v, t) => (
              <Badge tone={t.is_active ? "success" : "neutral"}>{t.is_active ? "In use" : "Switched off"}</Badge>
            ),
          },
          {
            key: "actions",
            label: "",
            sortable: false,
            render: (_v, t) => (
              <span className={styles.actions}>
                <TextButton onClick={() => setDialog({ kind: "edit", type: t })}>Edit</TextButton>
                {t.is_system ? null : (
                  <TextButton className={styles.danger} onClick={() => setDialog({ kind: "remove", type: t })}>
                    Remove
                  </TextButton>
                )}
              </span>
            ),
          },
        ]}
      />
      {dialog?.kind === "edit" ? <TypeDialog type={dialog.type} onClose={() => setDialog(null)} /> : null}
      {dialog?.kind === "remove" ? (
        <ConfirmDialog
          title={`Remove ${dialog.type.name}?`}
          description="Only a charge type no rule or charge has used can be removed. Otherwise, switch it off under Edit."
          action="Remove"
          danger
          run={() => pricingActions.removeType(dialog.type.id)}
          onDone={() => queryClient.invalidateQueries({ queryKey: ["pricing"] })}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </div>
  );
}

function TypeDialog({ type, onClose }: { type?: ChargeType; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(type?.name ?? "");
  const [code, setCode] = useState(type?.code ?? "");
  const [category, setCategory] = useState<Category>(type?.category ?? "surcharge");
  const [direction, setDirection] = useState(type?.direction ?? "receivable");
  const [payee, setPayee] = useState<Payee>(type?.payee ?? "company");
  const [active, setActive] = useState(type?.is_active ?? true);
  const system = type?.is_system ?? false;
  const save = useMutation({
    mutationFn: () =>
      type
        ? pricingActions.updateType(
            type.id,
            system ? { name: name.trim() } : { name: name.trim(), category, direction, payee, is_active: active },
          )
        : pricingActions.createType({ code, name: name.trim(), category, direction, payee }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["pricing"] });
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title={type ? `Edit ${type.name}` : "Add a charge type"}
        description={
          system
            ? "A system type: pricing and the books rely on it, so only its name changes."
            : "A new fee, surcharge, cost or tax. Then add rules for it."
        }
      >
        <div className={styles.form}>
          <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Weekend surcharge" />
          {type ? null : (
            <Input
              label="Code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="weekend_surcharge"
              description="Lowercase letters, digits and _. Can't change later."
            />
          )}
          {system ? null : (
            <>
              <div className={styles.pair}>
                <Select
                  label="Kind"
                  value={category}
                  onValueChange={(v) => setCategory(v as Category)}
                  options={(Object.keys(CATEGORY) as Category[]).map((c) => ({ value: c, label: CATEGORY[c] }))}
                />
                <Select
                  label="Money"
                  value={direction}
                  onValueChange={(v) => setDirection(v as "receivable" | "payable")}
                  options={[
                    { value: "receivable", label: "Charged to the customer" },
                    { value: "payable", label: "Paid out (a cost)" },
                  ]}
                />
              </div>
              {direction === "payable" ? (
                <Select
                  label="Paid to"
                  value={payee}
                  onValueChange={(v) => setPayee(v as Payee)}
                  options={(Object.keys(PAYEE) as Payee[]).map((p) => ({ value: p, label: PAYEE[p] }))}
                />
              ) : null}
              {type ? (
                <Switch label="In use: new rules can apply it" checked={active} onCheckedChange={setActive} />
              ) : null}
            </>
          )}
          {save.error ? (
            <Alert tone="danger" title="Not saved">
              {messageFor(errorOf(save.error))}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              disabled={!name.trim() || (!type && !/^[a-z0-9_]{1,40}$/.test(code))}
              loading={save.isPending}
              onClick={() => save.mutate()}
            >
              {type ? "Save" : "Add charge type"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
